import { useMemo, useState } from "react";
import { favoritosPrimeiro } from "@bookdental/schemas";
import { Input } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useFavoritos } from "@/lib/use-favoritos";
import { EstrelaDeFavorito } from "@/components/shared/EstrelaDeFavorito";
import { reais } from "./use-plano";

export type Procedimento = {
  id: string;
  name: string;
  especialidade: { code?: string; label: string };
  priceCents: number;
  color: string | null;
  cobradoPorDente: boolean;
  unidade?: string | null;
};

/** Procedimento de harmonização (especialidade principal) — só ele aparece no modo Face, e fica fora do modo Dentes. */
export const ehDaFace = (p: Pick<Procedimento, "especialidade">) => p.especialidade.code === "HARMONIZACAO_OROFACIAL";

const semAcento = (t: string) => t.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/**
 * Nome do procedimento: digita livre ou escolhe do catálogo (favoritos primeiro). Escolher traz o
 * preço do catálogo; digitar outra coisa desfaz o vínculo e vira item livre.
 */
export function SeletorDeProcedimento({
  nome,
  servicoId,
  modoFace = false,
  onMudarNome,
  onEscolher,
}: {
  nome: string;
  servicoId: string | null;
  /** Busca primeiro nos procedimentos do modo (Face: harmonização; Dentes: o resto). */
  modoFace?: boolean;
  onMudarNome: (nome: string) => void;
  onEscolher: (p: Procedimento) => void;
}) {
  const lista = trpc.plano.procedimentos.useQuery(undefined, { staleTime: 60_000 });
  const favoritos = useFavoritos("procedimento");
  const [aberto, setAberto] = useState(false);
  const sugestoes = useMemo(() => {
    const termo = semAcento(nome.trim());
    const todos = (lista.data ?? []) as Procedimento[];
    const busca = (base: Procedimento[]) => (termo ? base.filter((p) => semAcento(`${p.name} ${p.especialidade.label}`).includes(termo)) : base);
    // Sem nada do modo pro termo digitado, procura no catálogo inteiro.
    const doModo = busca(todos.filter((p) => ehDaFace(p) === modoFace));
    const achados = doModo.length || !termo ? doModo : busca(todos);
    return favoritosPrimeiro(achados, favoritos.ids).slice(0, 8);
  }, [lista.data, nome, favoritos.ids, modoFace]);
  const temCatalogo = (lista.data?.length ?? 0) > 0;

  return (
    <div className="relative">
      <Input
        id="item-procedimento"
        value={nome}
        autoComplete="off"
        placeholder={temCatalogo ? "Buscar no catálogo ou digitar" : "Ex.: Restauração em resina"}
        onFocus={() => setAberto(true)}
        onBlur={() => setTimeout(() => setAberto(false), 150)}
        onChange={(e) => {
          onMudarNome(e.target.value);
          setAberto(true);
        }}
      />
      {servicoId && <p className="mt-1 text-[11px] text-emerald-700">Do catálogo de procedimentos</p>}
      {aberto && temCatalogo && sugestoes.length > 0 && (
        <div className="absolute left-0 right-0 top-10 z-50 max-h-64 overflow-auto rounded-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
          {sugestoes.map((p) => (
            <div key={p.id} className="group flex items-center pr-2 hover:bg-brand-50 dark:hover:bg-primary/10">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onEscolher(p);
                  setAberto(false);
                }}
                className="flex min-w-0 flex-1 items-center justify-between gap-2 px-3 py-2 text-left"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-zinc-900 dark:text-foreground">{p.name}</span>
                  <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">{p.especialidade.label}</span>
                </span>
                <span className="shrink-0 text-xs text-zinc-500">{p.priceCents ? reais(p.priceCents) : "Sem preço"}</span>
              </button>
              <EstrelaDeFavorito ativo={favoritos.ids.has(p.id)} onAlternar={() => favoritos.alternar(p.id)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
